import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';

import { accessLogPage } from '../api/records.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant } from '../patients/format.ts';
import { ACCESS_KIND_WORDS, actionWords } from './labels.ts';
import { LoadFailed } from './records-page.tsx';
import styles from './records.module.css';

// A patient's access log (docs/plans/phase2-records-data-rights.md §3.4, §6): who viewed,
// changed, printed or exported the record, from the append-only audit log, newest first.
// Opening it is itself audited.

const route = getRouteApi('/_app/patients/$patientId/access-log');

const KIND_TAGS = {
  viewed: 'neutral',
  changed: 'active',
  printed: 'done',
  exported: 'attention',
  other: 'pending',
} as const;

export function AccessLogPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'records.access_log.read');
  const log = useInfiniteQuery({
    queryKey: ['records', 'access-log', patientId],
    queryFn: ({ pageParam }) => accessLogPage(patientId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: allowed,
  });
  const items = log.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Access log"
        description="Who opened, changed, printed or exported this patient's record. Opening this log is recorded too."
      />
      {!allowed ? (
        <Notice tone="info">
          You don't have permission to see access logs. Ask the medical records office.
        </Notice>
      ) : (
        <Section title="Events" description="Newest first. Times in East Africa Time.">
          {log.isPending ? <LoadingBlock label="Loading the access log" rows={5} /> : null}
          {log.isError ? (
            <LoadFailed
              error={log.error}
              fallback="The access log couldn't be loaded."
              onRetry={() => void log.refetch()}
            />
          ) : null}
          {log.isSuccess ? (
            <TableScroll>
              <Table flat aria-label="Access log">
                <TableHeader>
                  <Column isRowHeader>When</Column>
                  <Column>Who</Column>
                  <Column>What</Column>
                  <Column>Action</Column>
                  <Column>Where</Column>
                  <Column>Reason</Column>
                </TableHeader>
                <TableBody
                  items={items}
                  renderEmptyState={() => 'Nothing recorded for this patient yet.'}
                >
                  {(e) => (
                    <Row id={e.id}>
                      <Cell>{formatInstant(e.occurredAt)}</Cell>
                      <Cell>{e.actor.displayName}</Cell>
                      <Cell>
                        <Tag state={KIND_TAGS[e.kind]}>{ACCESS_KIND_WORDS[e.kind]}</Tag>
                      </Cell>
                      <Cell>{actionWords(e.action)}</Cell>
                      <Cell>{e.facility?.name ?? ''}</Cell>
                      <Cell>{e.reason ?? ''}</Cell>
                    </Row>
                  )}
                </TableBody>
              </Table>
            </TableScroll>
          ) : null}
          {log.hasNextPage ? (
            <div className={styles.actions}>
              <Button
                isDisabled={!online}
                isPending={log.isFetchingNextPage}
                pendingLabel="Loading…"
                onPress={() => void log.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </Section>
      )}
    </div>
  );
}
